import React, { useEffect, useState } from 'react';
import Image from 'next/image';
import Head from 'next/head';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Play, X, Calendar } from 'lucide-react';

import { getVideos } from 'context/actions/videos';
import PageHeader from 'components/PageHeader';

const CATEGORIES = [
    { key: '', label_mn: 'Бүгд', label_en: 'All' },
    { key: 'event', label_mn: 'Үйл явдал', label_en: 'Events' },
    { key: 'progress', label_mn: 'Төслийн явц', label_en: 'Progress' },
    { key: 'interview', label_mn: 'Ярилцлага', label_en: 'Interviews' },
];

export default function VideosPage() {
    const { t } = useTranslation();
    const lang = useSelector((s) => s.system.lang || 'mn');

    const [videos, setVideos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState('');
    const [active, setActive] = useState(null); // modal-д харагдах видео

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            const res = await getVideos(filter || null);
            if (cancelled) return;
            if (res?.data) setVideos(res.data);
            setLoading(false);
        })();
        return () => {
            cancelled = true;
        };
    }, [filter]);

    // ESC товч дармагц modal хаах
    useEffect(() => {
        const onEsc = (e) => {
            if (e.key === 'Escape') setActive(null);
        };
        if (active) {
            window.addEventListener('keydown', onEsc);
            document.body.style.overflow = 'hidden';
        }
        return () => {
            window.removeEventListener('keydown', onEsc);
            document.body.style.overflow = '';
        };
    }, [active]);

    const title = lang === 'en' ? 'Video Archive' : 'Видео архив';

    return (
        <>
            <Head>
                <title>{`${title} — Mongol Refinery`}</title>
            </Head>

            <PageHeader slug="videos" title={title} />

            <section className="py-16 lg:py-20 bg-brand-cream">
                <div className="max-w-[1600px] mx-auto px-4 lg:px-8">
                    {/* Category filter */}
                    <div className="flex flex-wrap gap-2 mb-10">
                        {CATEGORIES.map((c) => (
                            <button
                                key={c.key}
                                onClick={() => setFilter(c.key)}
                                className={`px-5 py-2.5 text-sm font-bold uppercase tracking-wider transition ${
                                    filter === c.key
                                        ? 'bg-brand-dark text-white'
                                        : 'bg-white text-brand-dark hover:bg-brand-yellow hover:text-brand-dark'
                                }`}
                            >
                                {lang === 'en' ? c.label_en : c.label_mn}
                            </button>
                        ))}
                    </div>

                    {/* Grid */}
                    {loading ? (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {Array.from({ length: 6 }).map((_, i) => (
                                <div
                                    key={i}
                                    className="bg-white animate-pulse aspect-[16/10]"
                                />
                            ))}
                        </div>
                    ) : videos.length === 0 ? (
                        <div className="text-center py-20 text-brand-gray">
                            {t('common.noData')}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {videos.map((v) => (
                                <VideoCard
                                    key={v.id}
                                    video={v}
                                    lang={lang}
                                    onPlay={() => setActive(v)}
                                />
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* ─── Modal видео тоглуулагч ─── */}
            {active && (
                <div
                    className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4"
                    onClick={() => setActive(null)}
                >
                    <button
                        className="absolute top-6 right-6 w-12 h-12 flex items-center justify-center bg-white/10 hover:bg-brand-yellow hover:text-brand-dark text-white transition"
                        aria-label="Хаах"
                        onClick={() => setActive(null)}
                    >
                        <X size={20} />
                    </button>
                    <div
                        className="w-full max-w-5xl aspect-video bg-black"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <iframe
                            src={`${active.embedUrl}?autoplay=1&rel=0`}
                            title={active.title}
                            className="w-full h-full border-0"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </div>
                </div>
            )}
        </>
    );
}

function VideoCard({ video, lang, onPlay }) {
    const title = lang === 'en' && video.titleEn ? video.titleEn : video.title;
    const thumb = video.thumbnailUrl || '/placeholders/news-default.svg';

    return (
        <button
            onClick={onPlay}
            className="group relative flex flex-col bg-white overflow-hidden text-left hover:shadow-xl transition-all"
        >
            <div className="relative aspect-[16/10] bg-brand-dark overflow-hidden">
                <Image
                    src={thumb}
                    alt={title}
                    fill
                    sizes="(max-width: 768px) 100vw, 33vw"
                    className="object-cover group-hover:scale-105 transition-transform duration-500"
                    unoptimized
                />
                <div className="absolute inset-0 bg-brand-dark/30 group-hover:bg-brand-dark/50 transition" />

                {/* Play icon */}
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-16 h-16 flex items-center justify-center bg-brand-yellow text-brand-dark group-hover:scale-110 transition">
                        <Play size={28} fill="currentColor" strokeWidth={0} />
                    </div>
                </div>
            </div>
            <div className="p-5 flex-1">
                <h3 className="text-base font-bold text-brand-dark leading-snug line-clamp-2 group-hover:text-brand-yellow-dark transition">
                    {title}
                </h3>
            </div>
        </button>
    );
}